import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";

import type { CampaignSchema } from "@/types";
import { Timer } from "lucide-react";
import type { TFunction } from "i18next";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

type CountdownState =
  | { mode: "finished" }
  | { mode: "dhm"; days: number; hours: number; minutes: number }
  | { mode: "hms"; hours: number; minutes: number; seconds: number };

function computeRemaining(endDate: string): CountdownState {
  const ms = new Date(endDate).getTime() - Date.now();
  if (ms <= 0) return { mode: "finished" };

  const days = Math.floor(ms / DAY);
  const hours = Math.floor((ms % DAY) / HOUR);
  const minutes = Math.floor((ms % HOUR) / MINUTE);
  const seconds = Math.floor((ms % MINUTE) / SECOND);

  if (ms >= DAY) return { mode: "dhm", days, hours, minutes };
  return { mode: "hms", hours, minutes, seconds };
}

const sameCountdown = (a: CountdownState, b: CountdownState): boolean =>
  a.mode === b.mode &&
  (a.mode === "finished" ||
    (a.mode === "dhm" &&
      b.mode === "dhm" &&
      a.days === b.days &&
      a.hours === b.hours &&
      a.minutes === b.minutes) ||
    (a.mode === "hms" &&
      b.mode === "hms" &&
      a.hours === b.hours &&
      a.minutes === b.minutes &&
      a.seconds === b.seconds));

/** Milliseconds until what the countdown SHOWS next changes, or null once it has ended. A day
 *  or more out it shows days/hours/minutes, so it only needs to wake on the minute (and at the
 *  switch into the last day); inside the last day it shows seconds and wakes every second. */
function nextTickMs(endDate: string): number | null {
  const ms = new Date(endDate).getTime() - Date.now();
  if (ms <= 0) return null;
  // +20ms so the wake-up lands just past the boundary, not a hair before it.
  if (ms >= DAY) return Math.min(ms % MINUTE || MINUTE, ms - DAY + 1) + 20;
  return (ms % SECOND || SECOND) + 20;
}

function useCountdown(endDate: string) {
  const [state, setState] = useState(() => computeRemaining(endDate));

  useEffect(() => {
    // Resync immediately when the campaign (endDate) changes, then wake at the display's own
    // resolution — per minute for a multi-day countdown, per second in the last day — and only
    // commit a render when the shown value actually changed. (Ticking every second regardless
    // was the only work an idle open widget did.)
    const update = () =>
      setState((prev) => {
        const next = computeRemaining(endDate);
        return sameCountdown(prev, next) ? prev : next;
      });
    update();
    let id: number | undefined;
    const schedule = () => {
      const delay = nextTickMs(endDate);
      if (delay === null) return;
      id = window.setTimeout(() => {
        update();
        schedule();
      }, delay);
    };
    schedule();
    return () => window.clearTimeout(id);
  }, [endDate]);

  return state;
}

type Segment = { value: number; label: string };

/** The three time units to show, given the current countdown state. */
function toSegments(state: CountdownState, t: TFunction): Segment[] {
  if (state.mode === "dhm") {
    return [
      { value: state.days, label: t("countdown.days") },
      { value: state.hours, label: t("countdown.hrs") },
      { value: state.minutes, label: t("countdown.min") },
    ];
  }
  if (state.mode === "hms") {
    return [
      { value: state.hours, label: t("countdown.hrs") },
      { value: state.minutes, label: t("countdown.min") },
      { value: state.seconds, label: t("countdown.sec") },
    ];
  }
  return [];
}

const AnimatedDigit = ({ value }: { value: string }) => (
  <div className="relative flex h-8 w-4 items-center justify-center">
    <AnimatePresence mode="popLayout">
      <motion.span
        key={value}
        // Transform/opacity only — a blur filter here forced an expensive
        // repaint on every seconds tick once a campaign entered its last 24h.
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ type: "spring", duration: 0.35, bounce: 0 }}
        className="text-text absolute inset-0 flex items-center justify-center font-heading text-sm font-semibold tabular-nums"
      >
        {value}
      </motion.span>
    </AnimatePresence>
  </div>
);

const TimeSegment = ({ value, label }: Segment) => {
  const [tens, ones] = String(value).padStart(2, "0").split("");
  return (
    <div className="flex flex-col items-center">
      <div className="flex gap-0">
        <AnimatedDigit value={tens} />
        <AnimatedDigit value={ones} />
      </div>
      <span className="font-body text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {label}
      </span>
    </div>
  );
};

// CSS blink (see index.css) — a perpetual decoration must not hold a motion
// loop open for the lifetime of every campaign card.
const Separator = () => (
  <div className="countdown-blink mb-4 flex flex-col items-center justify-center gap-1.5">
    <div className="size-0.5 rounded-full bg-muted-foreground" />
  </div>
);

const CountdownSegments = ({
  segments,
  layoutKey,
}: {
  segments: Segment[];
  layoutKey: string;
}) => {
  const [a, b, c] = segments;
  return (
    <motion.div
      key={layoutKey}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex w-full flex-col items-baseline justify-baseline gap-2"
    >
      <div className="flex w-full flex-row items-center justify-between">
        <TimeSegment value={a.value} label={a.label} />
        <Separator />
        <TimeSegment value={b.value} label={b.label} />
        <Separator />
        <TimeSegment value={c.value} label={c.label} />
      </div>
    </motion.div>
  );
};

/** Compact header timer — lucide icon + DAYS/HOURS/MIN (number over label). Figma 5129:6042. */
const HeaderCountdown = ({
  segments,
  ended,
  endedLabel,
}: {
  segments: Segment[];
  ended: boolean;
  endedLabel: string;
}) => (
  // Kept short (no vertical padding, small type) so it never grows the nav header.
  <div className="flex items-center gap-2">
    <Timer className="size-5 shrink-0 text-muted-foreground" strokeWidth={2} />
    {ended ? (
      <span className="font-heading text-[12px] text-muted-foreground">
        {endedLabel}
      </span>
    ) : (
      <div className="flex items-start gap-3 lg:gap-4">
        {segments.map((s) => (
          <div
            key={s.label}
            className="flex flex-col items-center leading-none"
          >
            <span className="font-heading text-[13px] leading-none font-normal text-foreground tabular-nums lg:text-[14px]">
              {String(s.value).padStart(2, "0")}
            </span>
            <span className="mt-0.5 font-body text-[9px] leading-none text-muted-foreground uppercase">
              {s.label}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const CampaignCountdown = ({
  campaign,
  variant = "card",
  compact = false,
  className,
}: {
  campaign: CampaignSchema;
  /** `inline`: one line of text ("Ends in 12 hrs 28 min") for tight rows like the in-game dock. */
  variant?: "card" | "header" | "inline";
  /** `inline` only: drop the "Ends in" prefix where the row can't afford it — the timer icon
   *  carries the meaning (the in-game dock, next to a type badge). */
  compact?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const rawState = useCountdown(campaign.end);
  // Only a FINISHED status says "Ended". A campaign the server still lists as ACTIVE whose end
  // has passed (the finishing tick has not run yet) holds a zero clock — the same rule as the
  // card's duration badge, so nothing under "Active" ever reads "Ended".
  const overrun = rawState.mode === "finished" && campaign.status !== "FI";
  const state: CountdownState = overrun
    ? { mode: "hms", hours: 0, minutes: 0, seconds: 0 }
    : rawState;
  const segments = toSegments(state, t);
  const ended = state.mode === "finished";

  if (variant === "inline") {
    // Two most significant units only — the third is noise at this size, and the seconds tick
    // would make a one-liner jitter.
    const [a, b] = segments;
    return (
      <span
        className={cn(
          "inline-flex min-w-0 items-center gap-1.5 font-body text-xs leading-none",
          className
        )}
      >
        <Timer
          className="size-3.5 shrink-0"
          strokeWidth={2}
          aria-hidden="true"
        />
        <span className="truncate">
          {ended ? (
            t("campaign.ended")
          ) : (
            <>
              {!compact && <>{t("campaign.endsInShort")} </>}
              <span className="font-heading font-semibold text-foreground tabular-nums">
                {a.value} {a.label} {b.value} {b.label}
              </span>
            </>
          )}
        </span>
      </span>
    );
  }

  if (variant === "header") {
    return (
      <div className={cn("flex", className)}>
        <HeaderCountdown
          segments={segments}
          ended={ended}
          endedLabel={t("campaign.ended")}
        />
      </div>
    );
  }

  return (
    <div className={cn("flex w-full", className)}>
      <AnimatePresence mode="wait">
        {ended ? (
          <motion.div
            key="ended"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-1.5"
          >
            <Timer className="size-3.5 text-muted-foreground" />
            {t("campaign.ended")}
          </motion.div>
        ) : (
          <CountdownSegments layoutKey={state.mode} segments={segments} />
        )}
      </AnimatePresence>
    </div>
  );
};
